import { useState } from 'react'

import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import PauseCircleOutline from '@mui/icons-material/PauseCircleOutlined'
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay'
import SendIcon from '@mui/icons-material/Send'
import StorageIcon from '@mui/icons-material/Storage'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { DatePicker } from '@mui/x-date-pickers'

import { addMonths, formatDate } from '@util/date-util'

import type { Project } from '../../../aansluiting/project/types/Project'
import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import { SelectString } from '../../../core/components/SelectString'
import type { HoofdleidingProject } from '../../../hoofdleiding/project/types/HoofdleidingProject'
import { ConfirmButton } from '../../../theme/components/ConfirmButton'
import { PendingIcon } from '../../../theme/components/PendingIcon'
import { type Step, Stepper } from '../../../theme/components/Stepper'
import { StyledError } from '../../../theme/components/StyledError'
import { useUpdateSubprocesForEntity } from '../hooks/useUpdateSubprocesForEntity'
import type { Subproces } from '../types/Subproces'
import {
  canEditNetcheck,
  generateNetcheckStepStatuses,
} from '../util/subproces-netcheck-util'
import {
  createSubprocesUpdate,
  getPausedInformation,
  getStatusLabelForType,
  isStepDisabled,
} from '../util/subproces-util'

import { SubprocesCard } from './SubprocesCard'

export { SubprocesNetcheck }

const NETCHECK_OPSTARTEN_MONTHS = 6

type Props = {
  project: Project | HoofdleidingProject
  subproces: Subproces
  subprocessen: Subproces[]
  user: AuthenticatedUser
  openLogDrawer: (subproces: Subproces) => void

  onClickCancel: () => void
  onClickPause: () => void
  onClickResume: () => void
  onClickHerplan: () => void
}

const SubprocesNetcheck = ({
  project,
  subproces,
  subprocessen,
  user,
  openLogDrawer,

  onClickCancel,
  onClickPause,
  onClickResume,
  onClickHerplan,
}: Props) => {
  const [dueDate, setDueDate] = useState<Date | null>(
    subproces.status === 'netcheck_opstarten'
      ? addMonths(new Date(), NETCHECK_OPSTARTEN_MONTHS)
      : null,
  )

  const {
    mutate: updateSubprocesForEntity,
    error,
    isPending,
  } = useUpdateSubprocesForEntity()

  const { statuses } = generateNetcheckStepStatuses({ subproces })
  const canEdit = canEditNetcheck({ user, subproces })
  const pausedInformation = getPausedInformation({ subproces })

  const getStepProps = (status: keyof typeof statuses) => ({
    label: getStatusLabelForType(subproces.type, status),
    status: statuses[status],
    disabled: isStepDisabled({ subproces, expectedStatus: status, isPending }),
  })

  const onCollectCustomerData = () => {
    const body = createSubprocesUpdate({
      subproces,
      status: 'netcheck_opstarten',
      assignedTo: selectedNetbeheerder,
      dueDate,
    })
    updateSubprocesForEntity(body)
  }

  const elektraNetbeheerders = project.disciplineVerzamelingen
    .filter((x) => x.disciplineLabel === 'Elektra')
    .map((x) => x.netbeheerderLabel)

  const hasMultipleNetbeheerders = elektraNetbeheerders.length > 1

  const [selectedNetbeheerder, setSelectedNetbeheerder] = useState<string>(
    hasMultipleNetbeheerders ? '' : elektraNetbeheerders[0],
  )

  const onStartNetcheck = () => {
    const body = createSubprocesUpdate({
      subproces,
      status: 'in_progress',
      assignedTo: selectedNetbeheerder,
      dueDate,
    })
    updateSubprocesForEntity(body)
  }

  const onComplete = () => {
    const body = createSubprocesUpdate({ subproces, status: 'done' })
    updateSubprocesForEntity(body)
  }

  const datePicker = ({
    label,
    disabled,
    value,
  }: {
    label: string
    disabled: boolean
    value: Date | null
  }) => (
    <DatePicker
      disablePast
      label={label}
      disabled={disabled}
      value={value}
      onChange={(newValue) => setDueDate(newValue)}
      slotProps={{
        field: { clearable: true },
        textField: {
          variant: 'standard',
          sx: {
            minWidth: '400px',
            maxWidth: '100%',
          },
        },
      }}
    />
  )

  const criteriaKlantVerzamelen = getStepProps('criteria_klant_verzamelen')
  const netcheckOpstarten = getStepProps('netcheck_opstarten')
  const inProgress = getStepProps('in_progress')
  const done = getStepProps('done')

  const steps: Step[] = [
    {
      label: criteriaKlantVerzamelen.label,
      status: criteriaKlantVerzamelen.status,
      content: (
        <>
          <Typography>
            Verzamel alle benodigde klantgegevens voor de netcheck. Controleer
            alle informatie en bevestig dat deze compleet is.
          </Typography>

          {selectedNetbeheerder && (
            <Typography variant="body2" color="textSecondary">
              De netcheck wordt uitgevoerd door de elektra netbeheerder op dit
              project: {selectedNetbeheerder}.
            </Typography>
          )}

          {!selectedNetbeheerder && elektraNetbeheerders.length === 0 && (
            <Typography variant="body2" color="error">
              Er is geen elektra netbeheerder gekoppeld aan dit project. Voeg
              een elektra netbeheerder toe of annuleer het subproces om verder
              te gaan
            </Typography>
          )}

          {hasMultipleNetbeheerders && canEdit && (
            <SelectString
              options={elektraNetbeheerders}
              fullWidth
              label="Netbeheerder"
              value={selectedNetbeheerder}
              variant="standard"
              disableClearable
              onChange={(value) => setSelectedNetbeheerder(value)}
            />
          )}

          {datePicker({
            label: 'Deadline opstarten netcheck (optioneel)',
            disabled: criteriaKlantVerzamelen.disabled,
            value: dueDate,
          })}

          {canEdit && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<EventRepeatIcon />}
                variant="text"
                onClick={onClickHerplan}
                disabled={criteriaKlantVerzamelen.disabled}
              >
                Herplannen
              </Button>

              <ConfirmButton
                disabled={
                  !selectedNetbeheerder || criteriaKlantVerzamelen.disabled
                }
                variant="contained"
                startIcon={
                  <PendingIcon isPending={isPending} icon={<StorageIcon />} />
                }
                onConfirm={onCollectCustomerData}
              >
                Klantgegevens verzameld
              </ConfirmButton>
            </Box>
          )}
        </>
      ),
    },
    {
      label: netcheckOpstarten.label,
      status: netcheckOpstarten.status,
      content: (
        <>
          <Typography>
            Start de netcheck op n.a.v. de verzamelde klantgegevens.
          </Typography>

          {datePicker({
            label: 'Verwachting netcheck gereed (optioneel)',
            disabled: netcheckOpstarten.disabled,
            value: dueDate,
          })}

          {canEdit && (
            <ConfirmButton
              variant="contained"
              disabled={netcheckOpstarten.disabled}
              onConfirm={onStartNetcheck}
              startIcon={
                <PendingIcon isPending={isPending} icon={<SendIcon />} />
              }
            >
              Netcheck starten
            </ConfirmButton>
          )}
        </>
      ),
    },
    {
      label: inProgress.label,
      caption: pausedInformation,
      status: inProgress.status,
      content: (
        <>
          <Typography>
            De netcheck is in uitvoering.
            <br />
            Als er vermogen beschikbaar is, rond de netcheck af. Zo niet,
            pauzeer de netcheck met de juiste reden.
          </Typography>

          {canEdit && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="text"
                disabled={inProgress.disabled}
                onClick={onClickPause}
                startIcon={
                  <PendingIcon
                    isPending={isPending}
                    icon={<PauseCircleOutline />}
                  />
                }
              >
                Geen vermogen beschikbaar
              </Button>

              <ConfirmButton
                variant="contained"
                color="success"
                disabled={inProgress.disabled}
                onConfirm={onComplete}
                startIcon={
                  <PendingIcon
                    isPending={isPending}
                    icon={<PlaylistPlayIcon />}
                  />
                }
              >
                Vermogen beschikbaar
              </ConfirmButton>
            </Box>
          )}
        </>
      ),
    },
    {
      label: done.label,
      status: done.status,
      content: (
        <>
          {subproces.status === 'done' && subproces.completed && (
            <Typography>{`${getStatusLabelForType(
              subproces.type,
              subproces.status,
            )} op ${formatDate(subproces.completed.date)} door ${
              subproces.completed.organisatie
            }`}</Typography>
          )}
        </>
      ),
    },
  ]

  return (
    <SubprocesCard
      subproces={subproces}
      subprocessen={subprocessen}
      openLogDrawer={openLogDrawer}
      onClickCancel={onClickCancel}
      onClickPause={onClickPause}
      onClickResume={onClickResume}
      disablePause={subproces.status !== 'in_progress'}
      disableCancel={!project.verantwoordelijken.includes(user.organisatie)}
    >
      <Stepper steps={steps} />

      <StyledError error={error} sx={{ marginBottom: 0, marginTop: 2 }} />
    </SubprocesCard>
  )
}
